import { Product } from '../../types';
export const assembleGradient = (grads) => {
    let gradient = 'linear-gradient(180deg, ';
    grads.forEach((grad, idx) => {
        gradient = `${gradient}${grad.color} ${grad.percentage}%${idx !== Object.values(grads).length - 1 ? ', ' : ''}`;
    });
    return `${gradient})`;
};
export const hexToRgb = (hex) => {
    let cleanHex = hex.replace(/^#/, '');
    if (cleanHex.length === 3) {
        cleanHex = cleanHex
            .split('')
            .map((char) => char + char)
            .join('');
    }
    if (cleanHex.length !== 6) {
        throw new Error('Invalid hex color format. Expected format "#RGB" or "#RRGGBB".');
    }
    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 1)`;
};
export const defaultGradient = [
    {
        color: '#fcfeff',
        percentage: 0,
    },
    {
        color: '#fcfeff',
        percentage: 100,
    },
];
export const productGradient = [
    {
        color: '#DDE6FA',
        percentage: 0,
    },
    {
        color: '#fcfeff',
        percentage: 100,
    },
];
export const initialGradients = {
    ['home']: [
        {
            color: '#dde6fa',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['products']: [
        {
            color: '#DDE6FA',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['sectors']: [
        {
            color: '#ebdcf2',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['solutions']: [
        {
            color: '#d6f5f2',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['news']: [
        {
            color: '#FDF4E3',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['licensing']: [
        {
            color: '#fdeffa',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['contact']: [
        {
            color: '#FAFCEC',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['about']: [
        {
            color: '#d8eef7',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    [Product.ResiLite]: productGradient,
    [Product.KeraLite]: productGradient,
    [Product.ElastiLite]: productGradient,
    [Product.SeaLite]: productGradient,
    [Product.CollaLite]: productGradient,
    [Product.LamiLite]: productGradient,
    ['privacy-policy']: [
        {
            color: '#FFF0FD',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
    ['terms-conditions']: [
        {
            color: '#FFF0FD',
            percentage: 0,
        },
        {
            color: '#fcfeff',
            percentage: 100,
        },
    ],
};
/**
 * Adjusts the lightness of an RGB(A) color string to ensure it does not exceed 94%,
 * and returns the color in RGBA format with an alpha value of 1.
 * @param color - The input CSS color string in rgb() or rgba() format.
 * @returns The adjusted color string in rgba() format with alpha set to 1.
 */
export const adjustColorLightness = (color) => {
    // Regular expression to match rgb() or rgba() strings
    const rgbRegex = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/;
    const match = color.match(rgbRegex);
    if (!match) {
        throw new Error('Invalid rgb() or rgba() color string format.');
    }
    // Extract RGB components and parse them as integers
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    // Extract alpha component if present, otherwise default to 1
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
    // Validate RGB values are within the 0-255 range
    if ([r, g, b].some((value) => value < 0 || value > 255)) {
        throw new Error('RGB values must be in the range 0 to 255.');
    }
    // Convert RGB to HSL
    const { h, s, l } = rgbToHsl(r, g, b);
    // Clamp lightness to a maximum of 94%
    const adjustedL = Math.min(l, 93.7);
    // Convert HSL back to RGB
    const { r: adjustedR, g: adjustedG, b: adjustedB, } = hslToRgb(h, s, adjustedL);
    // Return the adjusted color in rgba() format with alpha set to 1
    return `rgba(${adjustedR}, ${adjustedG}, ${adjustedB}, 1)`;
};
/**
 * Converts RGB color values to HSL.
 * @param r - Red component (0-255).
 * @param g - Green component (0-255).
 * @param b - Blue component (0-255).
 * @returns The HSL representation.
 */
const rgbToHsl = (r, g, b) => {
    // Normalize RGB values to the range [0, 1]
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (delta !== 0) {
        s = delta / (1 - Math.abs(2 * l - 1));
        switch (max) {
            case rNorm:
                h = ((gNorm - bNorm) / delta) % 6;
                break;
            case gNorm:
                h = (bNorm - rNorm) / delta + 2;
                break;
            case bNorm:
                h = (rNorm - gNorm) / delta + 4;
                break;
        }
        h = Math.round(h * 60);
        if (h < 0)
            h += 360;
    }
    return { h, s: s * 100, l: l * 100 };
};
/**
 * Converts HSL color values to RGB.
 * @param h - Hue (0-360).
 * @param s - Saturation (0-100).
 * @param l - Lightness (0-100).
 * @returns The RGB representation.
 */
const hslToRgb = (h, s, l) => {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;
    let rPrime = 0;
    let gPrime = 0;
    let bPrime = 0;
    if (h >= 0 && h < 60) {
        rPrime = c;
        gPrime = x;
    }
    else if (h >= 60 && h < 120) {
        rPrime = x;
        gPrime = c;
    }
    else if (h >= 120 && h < 180) {
        gPrime = c;
        bPrime = x;
    }
    else if (h >= 180 && h < 240) {
        gPrime = x;
        bPrime = c;
    }
    else if (h >= 240 && h < 300) {
        rPrime = x;
        bPrime = c;
    }
    else if (h >= 300 && h < 360) {
        rPrime = c;
        bPrime = x;
    }
    const r = Math.round((rPrime + m) * 255);
    const g = Math.round((gPrime + m) * 255);
    const b = Math.round((bPrime + m) * 255);
    return { r, g, b };
};
